import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Wallet, ArrowRight, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/i18n";

export default function PaymentSuccess() {
  const { user, refreshUser } = useAuth();
  const { lang } = useLang();
  const [sp] = useSearchParams();
  const [polling, setPolling] = useState(true);
  const [prevCredits, setPrevCredits] = useState(null);
  const [creditsAdded, setCreditsAdded] = useState(0);

  useEffect(() => {
    setPrevCredits(user?.credits ?? 0);
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      const u = await refreshUser();
      if (u && prevCredits !== null && u.credits > prevCredits) {
        setCreditsAdded(u.credits - prevCredits);
        setPolling(false);
        clearInterval(interval);
      } else if (attempts >= 15) {
        setPolling(false);
        clearInterval(interval);
      }
    }, 2000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="payment-success-page">
      <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-green-950/50 border-2 border-green-500 mb-8">
        <CheckCircle2 size={56} className="text-green-400" />
      </div>

      <span className="eyebrow text-green-400">{lang === "tr" ? "BAŞARILI" : "SUCCESS"}</span>
      <h1 className="headline text-5xl sm:text-6xl mt-3 text-white" data-testid="success-title">
        {lang === "tr" ? "Ödeme tamamlandı!" : "Payment completed!"}
      </h1>
      <p className="mt-4 text-lg text-neutral-300">
        {lang === "tr"
          ? "Teşekkürler — ödemen başarıyla işlendi."
          : "Thank you — your payment was processed successfully."}
      </p>

      <div className="mt-10 bg-neutral-950 border border-neutral-800 rounded-sm p-8 text-left">
        {polling ? (
          <div className="flex items-center gap-3 text-neutral-400" data-testid="success-polling">
            <Loader2 className="animate-spin text-red-500" size={20} />
            <span>{lang === "tr" ? "API kullanım birimleriniz hesabınıza yükleniyor..." : "Loading your credits..."}</span>
          </div>
        ) : creditsAdded > 0 ? (
          <div data-testid="success-credits-added">
            <div className="flex items-center gap-2 mb-4">
              <Wallet size={18} className="text-red-500" />
              <span className="eyebrow text-red-500">{lang === "tr" ? "EKLENEN KREDİ" : "CREDITS ADDED"}</span>
            </div>
            <p className="headline text-5xl text-green-400">+{creditsAdded.toLocaleString()}</p>
            <p className="mt-3 text-sm text-neutral-400">
              {lang === "tr" ? "Yeni bakiyen:" : "New balance:"}{" "}
              <span className="text-white font-semibold">{user?.credits.toLocaleString()} {lang === "tr" ? "kredi" : "credits"}</span>
            </p>
          </div>
        ) : (
          <p className="text-neutral-400" data-testid="success-pending">
            {lang === "tr"
              ? "Ödemen alındı. Krediler birkaç dakika içinde hesabına eklenecek."
              : "Your payment was received. Credits will be added to your account shortly."}
          </p>
        )}
      </div>

      <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
        <Link
          to="/profile"
          className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-sm uppercase tracking-wider font-bold text-sm transition-colors"
          data-testid="success-go-profile"
        >
          {lang === "tr" ? "Profilime Git" : "Go to Profile"} <ArrowRight size={16} />
        </Link>
        <Link
          to="/"
          className="inline-flex items-center justify-center gap-2 bg-neutral-900 hover:bg-neutral-800 text-white px-6 py-3 rounded-sm uppercase tracking-wider font-bold text-sm transition-colors border border-neutral-800"
          data-testid="success-go-home"
        >
          {lang === "tr" ? "Anasayfa" : "Home"}
        </Link>
      </div>
    </div>
  );
}
